import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Lock, Eye, EyeOff } from 'lucide-react';
import { Toaster } from '@/components/ui/sonner';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

const Shell = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center p-4 font-tac-body" style={{ background: 'linear-gradient(135deg,#F4F2EE,#E6E6EA)' }}>
    <Toaster position="top-center" richColors />
    <div className="w-full" style={{ maxWidth: 440 }}>
      <div style={{ background: '#fff', borderRadius: 14, padding: '34px 32px', boxShadow: '0 18px 48px rgba(20,20,20,0.14)' }}>{children}</div>
    </div>
  </div>
);

const PwField = ({ label, value, onChange, show, setShow, hint }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ fontSize: 12.5, fontWeight: 600, color: '#55555C', marginBottom: 7 }}>{label}</div>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, border: '1px solid #E4E4E9', borderRadius: 9, background: '#FBFBFC', padding: '0 13px' }}>
      <Lock className="w-4 h-4" style={{ color: '#9A9AA0' }} />
      <input type={show ? 'text' : 'password'} placeholder="••••••••" value={value} onChange={onChange} required style={{ flex: 1, border: 'none', outline: 'none', background: 'transparent', fontSize: 14, color: '#141414', padding: '13px 0' }} />
      <button type="button" onClick={() => setShow((s) => !s)} style={{ background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex' }}>
        {show ? <EyeOff className="w-[17px] h-[17px]" style={{ color: '#9A9AA0' }} /> : <Eye className="w-[17px] h-[17px]" style={{ color: '#9A9AA0' }} />}
      </button>
    </div>
    {hint && <p style={{ fontSize: 11.5, color: '#9A9AA0', marginTop: 6 }}>{hint}</p>}
  </div>
);

const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [success, setSuccess] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) { toast.error('Invalid reset link'); navigate('/login'); }
  }, [token, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) { toast.error('Passwords do not match'); return; }
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, { token, new_password: password });
      setSuccess(true);
      toast.success('Password reset successfully!');
      setTimeout(() => navigate('/login'), 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <Shell>
        <div className="text-center">
          <div className="flex items-center justify-center mx-auto" style={{ width: 64, height: 64, borderRadius: '50%', background: '#DCFCE7', marginBottom: 20 }}>
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#15803D" strokeWidth="2.4"><path d="M20 6L9 17l-5-5" /></svg>
          </div>
          <div className="font-tac-display" style={{ fontWeight: 800, fontSize: 23, color: '#141414', letterSpacing: '-0.01em' }}>Password updated</div>
          <div style={{ fontSize: 13.5, color: '#55555C', lineHeight: 1.6, margin: '10px 0 24px' }}>Your password has been reset. Taking you to the sign-in page…</div>
          <div className="flex items-center justify-center gap-2.5 font-tac-mono" style={{ fontSize: 12, color: '#9A9AA0' }}>
            <span className="inline-block animate-spin" style={{ width: 16, height: 16, border: '2px solid #E4E4E9', borderTopColor: '#BF212F', borderRadius: '50%' }} />
            REDIRECTING…
          </div>
        </div>
      </Shell>
    );
  }

  return (
    <Shell>
      <img src="/turbo-logo-color.png" alt="Turbo 360" style={{ height: 26, width: 'auto', display: 'block', marginBottom: 22 }} />
      <div className="font-tac-display" style={{ fontWeight: 800, fontSize: 22, color: '#141414', letterSpacing: '-0.01em' }}>Set a new password</div>
      <div style={{ fontSize: 13.5, color: '#55555C', lineHeight: 1.6, margin: '8px 0 22px' }}>Choose a new password for your account.</div>
      <form onSubmit={handleSubmit}>
        <PwField label="New password" value={password} onChange={(e) => setPassword(e.target.value)} show={showPassword} setShow={setShowPassword} hint="At least 6 characters" />
        <PwField label="Confirm new password" value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} show={showConfirmPassword} setShow={setShowConfirmPassword} />
        <button type="submit" disabled={loading} className="font-tac-heading w-full text-white" style={{ background: '#BF212F', borderRadius: 9, padding: '14px 24px', fontWeight: 600, fontSize: 14, cursor: 'pointer', opacity: loading ? 0.7 : 1, marginTop: 6 }}>
          {loading ? 'Updating…' : 'Update password'}
        </button>
        <div className="text-center" style={{ marginTop: 16 }}>
          <button type="button" onClick={() => navigate('/login')} className="font-tac-heading" style={{ fontSize: 13, color: '#55555C', fontWeight: 600, background: 'transparent', border: 'none', cursor: 'pointer' }}>← Back to sign in</button>
        </div>
      </form>
    </Shell>
  );
};

export default ResetPasswordPage;
